import * as Sentry from '@sentry/react';
import {
  hostMatchmakingState,
  matchmakerState,
  playjectorHostModeState,
  adModeState,
  hostState,
  matchmakerOverrideState,
  steamInstallState,
  hostInputDominanceState,
  machineIdState,
} from '../../stores/shared';

/**
 * Send matchmaking diagnostics to Sentry
 * Use this to debug matchmaking issues on locked-down machines
 */
export function sendMatchmakingDiagnostics(context?: string): void {
  if (!Sentry.isInitialized()) {
    console.warn('[Matchmaking Diagnostics] Sentry not initialized');
    return;
  }

  const diagnostics = {
    context: context || 'manual',
    timestamp: new Date().toISOString(),
    machineId: machineIdState.currentState,

    // Main matchmaking state
    hostMatchmakingState: {
      matchmakeable: hostMatchmakingState.currentState.matchmakeable,
      guestAccount: hostMatchmakingState.currentState.guestAccount,
      globalMatchmakingToggle: hostMatchmakingState.currentState.globalMatchmakingToggle,
    },

    // Matchmaker state details
    matchmakerState: {
      state: matchmakerState.currentState.state,
      currentMatchInvitationId: matchmakerState.currentState.stateDetails?.currentMatchInvitationId,
      hasStateDetails: !!matchmakerState.currentState.stateDetails,
    },

    // Playjector host mode (critical for ad mode)
    playjectorHostModeState: {
      edition: playjectorHostModeState.currentState.edition,
      matchmakable: playjectorHostModeState.currentState.matchmakable,
      gameLaunched: playjectorHostModeState.currentState.gameLaunched,
      gameName: playjectorHostModeState.currentState.gameName,
      gameId: playjectorHostModeState.currentState.gameId,
      steamLaunched: playjectorHostModeState.currentState.steamLaunched,
      serverLogin: playjectorHostModeState.currentState.serverLogin,
      userLoggedIn: playjectorHostModeState.currentState.userLoggedIn,
    },

    // Ad mode config
    adModeState: {
      enabled: adModeState.currentState.enabled,
      steamId: adModeState.currentState.steamId,
      launchArgs: adModeState.currentState.launchArgs,
    },

    // Host state
    hostState: {
      status: hostState.currentState.status,
      edition: hostState.currentState.edition,
    },

    // Other matchmaking criteria
    matchmakerOverride: matchmakerOverrideState.currentState,
    steamInstall: steamInstallState.currentState.steamInstall,
    inputDominance: hostInputDominanceState.currentState,
  };

  // Send as Sentry breadcrumb (lightweight, attached to next error)
  Sentry.addBreadcrumb({
    category: 'matchmaking.diagnostics',
    message: `Matchmaking diagnostics: ${context || 'manual'}`,
    level: 'info',
    data: diagnostics,
  });

  // ALSO send as custom event (shows up immediately in Sentry dashboard)
  Sentry.captureMessage(`[Matchmaking Diagnostics] ${context || 'Manual dump'}`, {
    level: 'info',
    contexts: {
      matchmaking: diagnostics,
    },
    tags: {
      diagnostic_type: 'matchmaking',
      matchmakeable: String(diagnostics.hostMatchmakingState.matchmakeable),
      ad_mode: String(diagnostics.adModeState.enabled),
      game_launched: String(diagnostics.playjectorHostModeState.gameLaunched),
      edition: diagnostics.hostState.edition || 'unknown',
    },
  });

  console.log('[Matchmaking Diagnostics] Sent to Sentry:', context, diagnostics);
}

/**
 * Start automatic matchmaking diagnostics logging
 * Sends diagnostics on state changes and every 30 seconds
 */
export function startMatchmakingDiagnosticsMonitoring(): () => void {
  console.log('[Matchmaking Diagnostics] Starting automatic monitoring');

  const cleanupFunctions: (() => void)[] = [];

  // Send initial diagnostic
  sendMatchmakingDiagnostics('monitoring_started');

  // Monitor hostMatchmakingState changes
  const hostMatchmakingListener = hostMatchmakingState.addListener((state) => {
    sendMatchmakingDiagnostics(`hostMatchmakingState_changed: matchmakeable=${state.matchmakeable}`);
  });
  cleanupFunctions.push(() => hostMatchmakingState.removeListener(hostMatchmakingListener));

  // Monitor playjectorHostModeState changes (especially gameLaunched)
  const playjectorListener = playjectorHostModeState.addListener((state) => {
    sendMatchmakingDiagnostics(`playjectorHostModeState_changed: gameLaunched=${state.gameLaunched}`);
  });
  cleanupFunctions.push(() => playjectorHostModeState.removeListener(playjectorListener));

  // Monitor matchmakerState changes
  const matchmakerListener = matchmakerState.addListener((state) => {
    sendMatchmakingDiagnostics(`matchmakerState_changed: state=${state.state}`);
  });
  cleanupFunctions.push(() => matchmakerState.removeListener(matchmakerListener));

  // Periodic dump every 30 seconds
  const periodicInterval = setInterval(() => {
    sendMatchmakingDiagnostics('periodic_dump');
  }, 30000);
  cleanupFunctions.push(() => clearInterval(periodicInterval));

  // Return cleanup function
  return () => {
    console.log('[Matchmaking Diagnostics] Stopping monitoring');
    cleanupFunctions.forEach(fn => fn());
  };
}

/**
 * Burst diagnostic logging - logs every second for 20 seconds
 * Use this to track what happens during critical events like process restart
 */
export function burstMatchmakingDiagnostics(context: string, durationSeconds = 20): void {
  console.log(`[Matchmaking Diagnostics] Starting burst logging for ${durationSeconds}s: ${context}`);

  // Send initial diagnostic immediately
  sendMatchmakingDiagnostics(`${context} - burst_start`);

  let secondsElapsed = 0;
  const interval = setInterval(() => {
    secondsElapsed++;
    sendMatchmakingDiagnostics(`${context} - burst_T+${secondsElapsed}s`);

    if (secondsElapsed >= durationSeconds) {
      clearInterval(interval);
      sendMatchmakingDiagnostics(`${context} - burst_end`);
      console.log(`[Matchmaking Diagnostics] Burst logging complete: ${context}`);
    }
  }, 1000);
}

